import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useOrderDraft } from '../Components/Orders/OrderDraftContext';

// Helper to extract up to 5 size and price ranges from product
const getSizePriceRanges = (product) => {
    if (!product) return [];
    const ranges = [];
    for (let i = 1; i <= 5; i++) {
        const size = product[`Size${i}`] || product[`size${i}`];
        const price = product[`Price${i}`] || product[`price${i}`];
        if (size && price !== undefined && price !== null && price !== '') {
            const num = Number(price);
            const formattedPrice = !isNaN(num) ? num.toFixed(2) : String(price);
            ranges.push({
                size: String(size).trim(),
                price: formattedPrice,
            });
        }
    }
    return ranges;
};

const ProductCardList = ({ products, maxHeight = '75vh' }) => {
    const navigate = useNavigate();
    const { draft, setProducts, removeSelectedProduct } = useOrderDraft();
    const { selectedProducts } = draft;

    const handleCardClick = (product) => {
        if (selectedProducts.some((selected) => selected.sku === product.sku)) {
            removeSelectedProduct(product.sku);
        } else {
            setProducts(product);
        }

        // Navigate to SelectVariation
        navigate('/SelectVariation');
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 2 } }}>
            {/* Scrollable Container */}
            <Box
                sx={{
                    width: '100%',
                    maxHeight: maxHeight,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: 1,
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                    },
                }}
            >
                {(!products || products.length === 0) ? (
                    <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No products found matching your search.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ pb: 2 }}>
                        {products.map((product) => {
                            const isSelected = selectedProducts.some(
                                (selected) => selected.sku === product.sku
                            );
                            const ranges = getSizePriceRanges(product);

                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    lg={3}
                                    key={product._id || product.sku}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: '12px',
                                            border: isSelected
                                                ? '2px solid #ba0000'
                                                : '1px solid #dedede',
                                            backgroundColor: '#ffffff',
                                            overflow: 'hidden',
                                            boxShadow: isSelected
                                                ? '0 4px 14px rgba(186, 0, 0, 0.18)'
                                                : '0 2px 8px rgba(0, 0, 0, 0.05)',
                                            transition:
                                                'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), border-color 0.3s ease',
                                            '&:hover': {
                                                // transform: 'translateY(-5px)',
                                                boxShadow: isSelected
                                                    ? '0 16px 32px rgba(186, 0, 0, 0.26), 0 6px 12px rgba(0, 0, 0, 0.08)'
                                                    : '0 14px 28px -4px rgba(1, 88, 161, 0.2), 0 8px 16px -4px rgba(0, 0, 0, 0.08)',
                                                borderColor: isSelected ? '#ba0000' : '#0158a1',
                                                '& .product-card-img': {
                                                    transform: 'scale(1.05)',
                                                },
                                                '& .card-bottom-banner': {
                                                    bgcolor: '#004c8c',
                                                },
                                            },
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() => handleCardClick(product)}
                                            sx={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                justifyContent: 'space-between',
                                                height: '100%',
                                            }}
                                        >
                                            {/* Top Header Text */}
                                            <Box
                                                sx={{
                                                    pt: { xs: 1.75, sm: 2.25 },
                                                    px: { xs: 1.25, sm: 1.75 },
                                                    pb: 0.5,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {/* Style / Article Name */}
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                                                        color: '#18364f',
                                                        letterSpacing: '0.3px',
                                                        lineHeight: 1.2,
                                                        textTransform: 'uppercase',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={
                                                        product.StyleFactory ||
                                                        product.styleFactory ||
                                                        product.ArticleName ||
                                                        product.Stylename ||
                                                        ''
                                                    }
                                                >
                                                    {product.StyleFactory ||
                                                        product.styleFactory ||
                                                        product.ArticleName ||
                                                        product.Stylename ||
                                                        'N/A'}
                                                </Typography>

                                                {/* SKU */}
                                                <Typography
                                                    variant="subtitle2"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: { xs: '0.85rem', sm: '0.92rem', md: '0.98rem' },
                                                        color: '#0a0a0a',
                                                        mt: 0.35,
                                                        lineHeight: 1.25,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {product.sku || 'N/A'}
                                                </Typography>

                                                {/* Color / Material */}
                                                <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.86rem' },
                                                        color: '#777777',
                                                        mt: 0.25,
                                                        lineHeight: 1.25,
                                                        textTransform: 'uppercase',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: '1.25em',
                                                    }}
                                                >
                                                    {product.Color ||
                                                        product.color_on_box ||
                                                        product.Material1 ||
                                                        '\u00a0'}
                                                </Typography>
                                            </Box>

                                            {/* Product Image Container */}
                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    width: '100%',
                                                    height: { xs: 210, sm: 185, md: 200, lg: 210 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    px: { xs: 1.5, sm: 2 },
                                                    py: { xs: 1, sm: 1.5 },
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    className="product-card-img"
                                                    image={product.Picture || '/blank-image.png'}
                                                    alt={
                                                        product.StyleFactory ||
                                                        product.sku ||
                                                        'Product'
                                                    }
                                                    sx={{
                                                        maxHeight: '100%',
                                                        maxWidth: '100%',
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                        transition: 'transform 0.3s ease-in-out',
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = '/blank-image.png';
                                                    }}
                                                />
                                            </Box>

                                            {/* Bottom Size & Price Banner */}
                                            <Box
                                                className="card-bottom-banner"
                                                sx={{
                                                    width: '100%',
                                                    bgcolor: '#0158a1',
                                                    color: '#ffffff',
                                                    py: { xs: 0.85, sm: 1 },
                                                    px: { xs: 0.75, sm: 1 },
                                                    minHeight: { xs: 38, sm: 42 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    transition: 'background-color 0.3s ease',
                                                }}
                                            >
                                                {ranges.length > 0 ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexWrap: 'wrap',
                                                            rowGap: 0.25,
                                                            columnGap: { xs: 0.4, sm: 0.6 },
                                                            fontWeight: 700,
                                                            letterSpacing:
                                                                ranges.length >= 3 ? '-0.2px' : 'normal',
                                                            fontSize:
                                                                ranges.length >= 3
                                                                    ? {
                                                                        xs: '0.68rem',
                                                                        sm: '0.72rem',
                                                                        md: '0.75rem',
                                                                        lg: '0.78rem',
                                                                    }
                                                                    : {
                                                                        xs: '0.78rem',
                                                                        sm: '0.82rem',
                                                                        md: '0.85rem',
                                                                    },
                                                            lineHeight: 1.25,
                                                        }}
                                                    >
                                                        {ranges.map((item, idx) => (
                                                            <React.Fragment key={idx}>
                                                                <Box
                                                                    component="span"
                                                                    sx={{ whiteSpace: 'nowrap' }}
                                                                >
                                                                    {item.size}: ${item.price}
                                                                </Box>
                                                                {idx < ranges.length - 1 && (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            opacity: 0.75,
                                                                            fontWeight: 400,
                                                                            userSelect: 'none',
                                                                        }}
                                                                    >
                                                                        |
                                                                    </Box>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#ffffff',
                                                            opacity: 0.85,
                                                            fontSize: { xs: '0.72rem', sm: '0.78rem' },
                                                        }}
                                                    >
                                                        Sizes Not Available
                                                    </Typography>
                                                )}
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default ProductCardList;
